// Default Configuration File
// This file contains all default configuration values for the application

import { getServerConfigSync } from './configLoader';

export interface DefaultConfig {
  // Server Configuration
  server: {
    host: string;
    port: number;
    protocol: 'http' | 'https';
    timeout: number;
    retries: number;
  };
  
  // Database Configuration
  database: {
    host: string;
    port: number;
    user: string;
    password: string;
    database: string;
    connectionLimit: number;
    waitForConnections: boolean;
  };
  
  // Application Configuration
  app: {
    name: string;
    version: string;
    environment: 'development' | 'production' | 'test';
    debug: boolean;
    logLevel: 'error' | 'warn' | 'info' | 'debug';
  };
  
  // UI Configuration
  ui: {
    theme: 'light' | 'dark' | 'auto';
    language: string;
    autoSave: boolean;
    autoSaveInterval: number;
    maxRecentItems: number;
  };
  
  // Flow Configuration
  flow: {
    defaultFlowName: string;
    maxNodesPerFlow: number;
    maxFlows: number;
    autoExecute: boolean;
    executionTimeout: number;
  };
  
  // Function Configuration
  functions: {
    maxCustomFunctions: number;
    defaultTimeout: number;
    allowExternalScripts: boolean;
    sandboxMode: boolean;
  };
  
  // Security Configuration
  security: {
    corsEnabled: boolean;
    allowedOrigins: string[];
    maxRequestSize: string;
    rateLimitEnabled: boolean;
    rateLimitWindow: number;
    rateLimitMax: number;
  };
}

// Load server configuration from JSON file
const loadServerConfigFromFile = (): DefaultConfig['server'] => {
  try {
    const serverConfig = getServerConfigSync();
    return {
      ...serverConfig,
      timeout: 30000, // 30 seconds
      retries: 3
    };
  } catch (error) {
    console.warn('Failed to load server config from JSON file, using defaults:', error);
    return {
      host: 'localhost',
      port: 5555,
      protocol: 'http',
      timeout: 30000, // 30 seconds
      retries: 3
    };
  }
};

// Default configuration values
export const defaultConfig: DefaultConfig = {
  // Server Configuration
  server: loadServerConfigFromFile(),
  
  // Database Configuration
  database: {
    host: '127.0.0.1',
    port: 3306,
    user: 'user',
    password: 'password',
    database: 'profiles',
    connectionLimit: 10,
    waitForConnections: true
  },
  
  // Application Configuration
  app: {
    name: 'Agent Test Environment',
    version: '1.0.0',
    environment: 'development',
    debug: true,
    logLevel: 'info'
  },
  
  // UI Configuration
  ui: {
    theme: 'light',
    language: 'en',
    autoSave: true,
    autoSaveInterval: 5000, // 5 seconds
    maxRecentItems: 10
  },
  
  // Flow Configuration
  flow: {
    defaultFlowName: 'FirstAuction',
    maxNodesPerFlow: 50,
    maxFlows: 100,
    autoExecute: false,
    executionTimeout: 60000 // 60 seconds
  },
  
  // Function Configuration
  functions: {
    maxCustomFunctions: 100,
    defaultTimeout: 10000, // 10 seconds
    allowExternalScripts: false,
    sandboxMode: true
  },
  
  // Security Configuration
  security: {
    corsEnabled: true,
    allowedOrigins: ['http://localhost:3000', 'http://localhost:3001'],
    maxRequestSize: '10mb',
    rateLimitEnabled: false,
    rateLimitWindow: 900000, // 15 minutes
    rateLimitMax: 100
  }
};

// Helper functions to get specific configuration sections
export const getServerConfig = () => defaultConfig.server;
export const getDatabaseConfig = () => defaultConfig.database;
export const getAppConfig = () => defaultConfig.app;
export const getUIConfig = () => defaultConfig.ui;
export const getFlowConfig = () => defaultConfig.flow;
export const getFunctionConfig = () => defaultConfig.functions;
export const getSecurityConfig = () => defaultConfig.security;

// Helper function to get a specific configuration value by path
export const getConfigValue = (path: string): any => {
  const keys = path.split('.');
  let value: any = defaultConfig;
  
  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key];
    } else {
      return undefined;
    }
  }
  
  return value;
};

// Helper function to merge custom configuration with defaults
export const mergeConfig = (customConfig: Partial<DefaultConfig>): DefaultConfig => {
  return {
    ...defaultConfig,
    ...customConfig,
    server: { ...defaultConfig.server, ...customConfig.server },
    database: { ...defaultConfig.database, ...customConfig.database },
    app: { ...defaultConfig.app, ...customConfig.app },
    ui: { ...defaultConfig.ui, ...customConfig.ui },
    flow: { ...defaultConfig.flow, ...customConfig.flow },
    functions: { ...defaultConfig.functions, ...customConfig.functions },
    security: { ...defaultConfig.security, ...customConfig.security }
  };
};

// Environment-specific configurations
export const getEnvironmentConfig = (): Partial<DefaultConfig> => {
  // For React apps, we'll default to development
  // In a real app, you might want to use window.location.hostname or other browser APIs
  return {
    app: {
      ...defaultConfig.app,
      environment: 'development',
      debug: true,
      logLevel: 'debug'
    }
  };
};

// Export the merged configuration for the current environment
export const config = mergeConfig(getEnvironmentConfig());

export default config; 